import { Check, ChevronDown } from 'lucide-react';
import { Children, isValidElement, useEffect, useRef, useState, type ChangeEvent, type ReactNode, type SelectHTMLAttributes } from 'react';

type Option = { value: string; label: ReactNode; disabled: boolean };

function extractOptions(children: ReactNode): Option[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>(child)) return [];
    return [{ value: String(child.props.value ?? ''), label: child.props.children, disabled: child.props.disabled ?? false }];
  });
}

/**
 * A fully custom listbox, not a native <select> — the browser's own option popover can't be
 * restyled (it renders as raw OS/browser chrome, jarring against the rest of the design
 * system) and was a recurring complaint. The public API still matches
 * SelectHTMLAttributes<HTMLSelectElement> (value/onChange(e)/children as <option>) so every
 * existing call site — `<Select value={x} onChange={(e) => setX(e.target.value)}><option .../></Select>` —
 * keeps working unmodified; only this file's internals changed.
 */
export function Select({ className = '', children, value, onChange, disabled, required, id }: SelectHTMLAttributes<HTMLSelectElement>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const options = extractOptions(children);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onPointerDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  function pick(v: string) {
    setOpen(false);
    onChange?.({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>);
  }

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="select flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className={`truncate ${current ? '' : 'text-muted'}`}>{current ? current.label : 'Select…'}</span>
        <ChevronDown size={18} className={`shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Visually hidden, keeps `required` participating in the surrounding <form>'s native validation. */}
      {required && <input tabIndex={-1} aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0 w-0 opacity-0" value={value ?? ''} required onChange={() => {}} />}

      {open && (
        <div role="listbox" className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-white p-1 shadow-lg">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              disabled={o.disabled}
              onClick={() => pick(o.value)}
              className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${o.value === value ? 'bg-brand-50 text-brand-700' : 'hover:bg-canvas'}`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && <Check size={14} className="shrink-0" />}
            </button>
          ))}
          {options.length === 0 && <p className="px-3 py-2 text-sm text-muted">No options.</p>}
        </div>
      )}
    </div>
  );
}
